import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@/auth/UserContext';
import { DetailLayout } from '@/components/ProfileMenu/DetailLayout';
import { FamilyMemberCard } from '@/components/ProfileMenu/FamilyMemberCard';
import { useFamilySettings } from '@/components/ProfileMenu/hooks/useFamilySettings';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import {
  PersonType,
  type AddPhoneContext,
  type FamilyMember,
} from '@/types/user';

const ROLE_ORDER = [
  PersonType.Student,
  PersonType.Mother,
  PersonType.Father,
  PersonType.OtherResponsiblePerson,
];

interface Props {
  onBack: () => void;
  onClose: () => void;
  onOpenPhone: (member: FamilyMember) => void;
  onOpenNotifications: (member: FamilyMember) => void;
  onOpenAdd: (context: AddPhoneContext) => void;
}

export const FamilyView: React.FC<Props> = ({
  onBack,
  onClose,
  onOpenPhone,
  onOpenNotifications,
  onOpenAdd,
}) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const { data: familyMembers, isLoading } = useFamilySettings();

  const isCurrentUser = (member?: FamilyMember) =>
    !!member?.userPublicId && member.userPublicId === user?.userPublicId;

  const studentUserPublicId = familyMembers?.find(
    (member) => member.personType === PersonType.Student,
  )?.userPublicId;

  const handleAdd = (personType: PersonType) => {
    if (!studentUserPublicId) {
      return;
    }

    onOpenAdd({
      personType,
      studentUserPublicId,
    });
  };

  // The backend returns a list, but a family holds at most one member per role
  // (one child, one mother, one father, one other), so we render a fixed card
  // per role — matching a member by type — rather than iterating the list.
  const items = ROLE_ORDER.map((personType) => ({
    personType,
    member: familyMembers?.find((member) => member.personType === personType),
  })).filter(({ member }) => !isCurrentUser(member));

  return (
    <DetailLayout
      title={t('familySettings.title')}
      onBack={onBack}
      onClose={onClose}
    >
      {isLoading && (
        <div className="flex flex-1 items-center justify-center">
          <PageSpinner className="size-[20px] text-subtle-foreground" />
        </div>
      )}

      {!isLoading && (
        <div className="mt-4 flex flex-col gap-3 md:mt-0">
          {items.map(({ personType, member }) => (
            <FamilyMemberCard
              key={personType}
              personType={personType}
              member={member}
              onAdd={() => handleAdd(personType)}
              onPhone={onOpenPhone}
              onNotifications={onOpenNotifications}
            />
          ))}
        </div>
      )}
    </DetailLayout>
  );
};
